import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';
import { supabase } from '../../lib/supabase';
import type { Article } from '../../types/article';
import { timeAgo } from '../../utils/timeAgo';

const CATEGORIES = ['ALL', 'WARZONE', 'MULTIPLAYER', 'ZOMBIES', 'ESPORTS', 'LEAKS', 'COMMUNITY'];

const categoryTag: Record<string, string> = {
  Warzone: 'WZ',
  Multiplayer: 'MP',
  Zombies: 'ZM',
  Esports: 'CDL',
  Community: 'COM',
  Leaks: 'LEAK',
};

const newsFeedSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Call of Duty News & Blog — CallOfDutyNext.com',
    description:
      'Browse all Call of Duty news, Warzone updates, patch notes analysis, CDL esports coverage, and community articles.',
    url: `${SITE_URL}/news`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'News', item: `${SITE_URL}/news` },
    ],
  },
];

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

function ArticleCard({ article, featured = false }: { article: Article; featured?: boolean }) {
  const slug = article.slug || article.id;
  return (
    <Link to={`/news/${slug}`} className="group block cursor-pointer">
      <div className={`overflow-hidden w-full ${featured ? 'h-72' : 'h-44'}`}>
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
            <i className="ri-image-line text-[#333] text-3xl" />
          </div>
        )}
      </div>
      <div className="pt-4 pb-6 border-b border-[#1e1e1e]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
            <Tag label="NEWS" />
          </div>
          <span className="text-[#666] text-xs font-['Barlow_Condensed'] font-bold tracking-wider">
            {timeAgo(article.date)}
          </span>
        </div>
        <h2 className={`font-['Barlow_Condensed'] font-black text-white uppercase leading-tight group-hover:text-[#00ff41] transition-colors ${featured ? 'text-3xl' : 'text-lg'}`}>
          {article.title}
        </h2>
        {featured && article.excerpt && (
          <p className="text-[#888] text-sm mt-2 line-clamp-2">{article.excerpt}</p>
        )}
        {featured && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Tag label={article.category.toUpperCase()} />
          </div>
        )}
      </div>
    </Link>
  );
}

function HorizontalCard({ article }: { article: Article }) {
  const slug = article.slug || article.id;
  return (
    <Link
      to={`/news/${slug}`}
      className="group flex gap-4 cursor-pointer border-b border-[#1e1e1e] py-5"
    >
      <div className="shrink-0 overflow-hidden" style={{ width: '130px', height: '86px' }}>
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-[#1a1a1a]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-['Barlow_Condensed'] font-black text-white text-sm leading-tight uppercase group-hover:text-[#00ff41] transition-colors mb-2 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-[#666] text-xs font-['Barlow_Condensed'] font-bold tracking-wider mb-2">
          {timeAgo(article.date)}
        </p>
        <div className="flex items-center gap-1.5">
          <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
        </div>
      </div>
    </Link>
  );
}

export default function NewsFeedPage() {
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(9);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('articles')
        .select('*')
        .eq('published', true)
        .order('date', { ascending: false });
      setArticles((data as Article[]) ?? []);
      setLoading(false);
    };
    fetchArticles();
  }, []);

  const filtered =
    activeCategory === 'ALL'
      ? articles
      : articles.filter((a) => a.category.toUpperCase() === activeCategory);

  const featured = filtered[0] ?? null;
  const secondary = filtered.slice(1, 3);
  const rest = filtered.slice(3, visibleCount + 3);

  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="Call of Duty News & Blog — Latest CoD Updates | CallOfDutyNext.com"
        description="All the latest Call of Duty news, Warzone updates, CDL esports recaps, and community articles. Browse by category: Warzone, Multiplayer, Zombies, Esports, Leaks."
        keywords="Call of Duty news, Warzone updates, CoD blog, CDL esports, multiplayer news, zombies, leaks"
        canonical="/news"
        schema={newsFeedSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* Page title bar */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider">
              Blog
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setVisibleCount(9); }}
                  className={`font-['Barlow_Condensed'] font-bold text-xs tracking-widest px-3 py-1.5 border transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-white text-black border-white'
                      : 'border-[#333] text-[#888] hover:border-[#666] hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-[#555]">
            <i className="ri-loader-4-line animate-spin text-3xl mr-3" /> Loading intel...
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-32 px-6">
            <i className="ri-article-line text-5xl text-[#333] mb-4 block" />
            <p className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase mb-2">No Articles Yet</p>
            <p className="text-[#666] text-sm">
              Head to <a href="/admin" className="text-[#00ff41] hover:underline">Admin</a> to publish your first article.
            </p>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 py-8">

            {/* Top: Featured + 2 secondary */}
            {featured && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-0">
                <div className="lg:col-span-2">
                  <ArticleCard article={featured} featured />
                </div>
                <div className="lg:col-span-1 space-y-0">
                  {secondary.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            )}

            {/* More Articles */}
            {rest.length > 0 && (
              <div className="mt-4">
                <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-0 pt-4 border-t border-[#1e1e1e]">
                  MORE NEWS
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8">
                  {rest.map((a) => <HorizontalCard key={a.id} article={a} />)}
                </div>
              </div>
            )}

            {/* View More button */}
            {filtered.length > visibleCount + 3 && (
              <div className="mt-10 flex justify-center">
                <button
                  onClick={() => setVisibleCount((c) => c + 9)}
                  className="font-['Barlow_Condensed'] font-black text-sm tracking-widest text-white border border-[#444] px-16 py-3 hover:bg-white hover:text-black transition-all cursor-pointer whitespace-nowrap"
                >
                  VIEW MORE
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
